'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Pusher from 'pusher-js'

type Notification = {
  id: string
  type: 'REPLY' | 'QUOTE' | 'WARNING' | 'SYSTEM' | 'MENTION'
  message: string | null
  isRead: boolean
  createdAt: string
  threadId: string | null
  actor: { name: string } | null
  thread: { title: string } | null
}

export default function NotificationBell({ currentUserId }: { currentUserId: string | null }) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 1. Fetch initial notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await fetch('/api/notifications')
        if (res.ok) {
          const data = await res.json()
          setNotifications(data.notifications)
          setUnreadCount(data.unreadCount)
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error)
      }
    }
    fetchNotifications()
  }, [])

  // 2. Pusher live subscription with diagnostic logs
  useEffect(() => {
    console.log("🔔 NotificationBell mounted. currentUserId:", currentUserId)
    if (!currentUserId) {
      console.warn("⚠️ NotificationBell: No currentUserId provided, live socket skipped.")
      return
    }

    let pusherClient: any = null
    let channel: any = null

    try {
      const pusherKey = process.env.NEXT_PUBLIC_PUSHER_KEY
      if (!pusherKey) {
        console.error("❌ NEXT_PUBLIC_PUSHER_KEY is missing on the frontend!")
        return
      }

      pusherClient = new Pusher(pusherKey, { cluster: 'eu' })
      const channelName = `user-${currentUserId}`
      console.log(`🔔 Subscribing to Pusher channel: ${channelName}`)
      
      channel = pusherClient.subscribe(channelName)

      channel.bind('pusher:subscription_succeeded', () => {
        console.log(`🟢 Successfully subscribed to ${channelName}`)
      })

      channel.bind('new-notification', (newNotif: Notification) => {
        console.log("📥 LIVE NOTIFICATION RECEIVED:", newNotif)
        setNotifications(prev => [newNotif, ...prev])
        setUnreadCount(prev => prev + 1)
      })
    } catch (err) {
      console.error("🔴 Pusher notification connection failed:", err)
    }

    return () => {
      if (pusherClient && channel) {
        pusherClient.unsubscribe(`user-${currentUserId}`)
      }
    }
  }, [currentUserId])

  // Close dropdown if user clicks outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = async () => {
    const willOpen = !isOpen
    setIsOpen(willOpen)

    if (willOpen && unreadCount > 0) {
      setUnreadCount(0)
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
      await fetch('/api/notifications', { method: 'PATCH' })
    }
  }

  const getNotificationContent = (n: Notification) => {
    if (n.type === 'WARNING') {
      return <span className="text-red-400 font-medium">⚠️ {n.message || 'You received a moderation strike.'}</span>
    }
    if (n.type === 'QUOTE') {
      return <span><strong className="text-indigo-400">{n.actor?.name}</strong> quoted you in <em className="text-slate-300">{n.thread?.title}</em></span>
    }
    if (n.type === 'REPLY') {
      return <span><strong className="text-indigo-400">{n.actor?.name}</strong> replied to your thread <em className="text-slate-300">{n.thread?.title}</em></span>
    }
    if (n.type === 'MENTION') {
      return <span><strong className="text-indigo-400">{n.actor?.name}</strong> mentioned you in <em className="text-slate-300">{n.thread?.title}</em></span>
    }
    return <span>{n.message}</span>
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={handleToggle}
        className="relative p-2 text-slate-400 hover:text-slate-200 transition-colors focus:outline-none rounded-sm hover:bg-slate-800"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-slate-950">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-[400px] overflow-y-auto rounded-sm bg-slate-900 border border-slate-800 shadow-2xl z-50">
          <div className="p-3 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-slate-200">Notifications</h3>
          </div>
          
          <div className="flex flex-col">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                You have no notifications yet.
              </div>
            ) : (
              notifications.map((n) => (
                <div key={n.id} className={`p-3 border-b border-slate-800/50 last:border-0 hover:bg-slate-800/50 transition-colors ${!n.isRead ? 'bg-slate-800/20' : ''}`}>
                  {n.threadId ? (
                    <Link href={`/thread/${n.threadId}`} onClick={() => setIsOpen(false)} className="block text-sm text-slate-300">
                      {getNotificationContent(n)}
                      <div className="text-xs text-slate-500 mt-1">{new Date(n.createdAt).toLocaleDateString()}</div>
                    </Link>
                  ) : (
                    <div className="block text-sm text-slate-300">
                      {getNotificationContent(n)}
                      <div className="text-xs text-slate-500 mt-1">{new Date(n.createdAt).toLocaleDateString()}</div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}